import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  authorityConfig,
  authorityLevelsSorted,
  type AuthorityLevel,
  type GlossaryEntry,
  type GovernanceRef,
} from "@iamu/shared";

const AUTHORITY_LEVEL_DESCRIPTIONS: Record<AuthorityLevel, string> = {
  legislation: "Laws passed by parliament or congress (hard constraint)",
  regulation: "Rules issued by government agencies (binding)",
  standard: "Industry standards (ISO, WCAG, NIST, etc.)",
  company_policy: "Internal organizational policies",
  guideline: "Recommended practices, not mandatory",
  informational: "Reference material and best practices",
};

interface AuthorityDialogProps {
  initialRef?: GovernanceRef;
  onSave: (ref: GovernanceRef) => void;
  onClose: () => void;
}

export function AuthorityDialog({
  initialRef,
  onSave,
  onClose,
}: AuthorityDialogProps) {
  const [name, setName] = useState(initialRef?.name ?? "");
  const [authorityLevel, setAuthorityLevel] = useState<AuthorityLevel>(
    initialRef?.authorityLevel ?? "regulation",
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initialRef
              ? "Edit governing authority"
              : "Add governing authority"}
          </DialogTitle>
          <DialogDescription>
            Specify a statutory law, regulatory agency rule, industry standard,
            or internal policy that governs your domain.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="auth-name">Authority, law, or standard name</Label>
            <Input
              id="auth-name"
              placeholder="e.g. Skatteverket representation rules, GDPR, AFS 2023:2, WCAG 2.2"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="auth-level">Authority tier</Label>
            <Select
              value={authorityLevel}
              onValueChange={(v) => setAuthorityLevel(v as AuthorityLevel)}
            >
              <SelectTrigger id="auth-level">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {authorityLevelsSorted.map((level) => (
                  <SelectItem key={level} value={level}>
                    <div className="flex flex-col py-0.5 text-left">
                      <span className="font-medium">
                        {authorityConfig[level].label}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {AUTHORITY_LEVEL_DESCRIPTIONS[level]}
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!name.trim()}
            onClick={() => {
              onSave({
                name: name.trim(),
                authorityLevel,
                ...(initialRef?.sourceId
                  ? { sourceId: initialRef.sourceId }
                  : {}),
              });
            }}
          >
            Save authority
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface GlossaryTermDialogProps {
  initialEntry?: GlossaryEntry;
  onSave: (draft: {
    canonicalTerm: string;
    translation?: string;
    definition?: string;
    variants: string[];
  }) => void;
  onClose: () => void;
}

export function GlossaryTermDialog({
  initialEntry,
  onSave,
  onClose,
}: GlossaryTermDialogProps) {
  const [canonicalTerm, setCanonicalTerm] = useState(
    initialEntry?.canonicalTerm ?? "",
  );
  const [translation, setTranslation] = useState(
    initialEntry?.translation ?? "",
  );
  const [definition, setDefinition] = useState(initialEntry?.definition ?? "");
  const [variantsText, setVariantsText] = useState(
    initialEntry?.variants.join(", ") ?? "",
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {initialEntry ? "Edit glossary term" : "Add glossary term"}
          </DialogTitle>
          <DialogDescription>
            Define a canonical domain term, its meaning, and any ambiguous
            synonyms walkthrough runners should avoid.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="glossary-term">Canonical term</Label>
            <Input
              id="glossary-term"
              placeholder="e.g. Representationsavdrag, Fika expense, Qualifying receipt"
              value={canonicalTerm}
              onChange={(e) => setCanonicalTerm(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="glossary-definition">Definition</Label>
            <Textarea
              id="glossary-definition"
              placeholder="What this term means in your domain and what business or regulatory rules apply..."
              value={definition}
              onChange={(e) => setDefinition(e.target.value)}
              rows={3}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="glossary-translation">Translation (optional)</Label>
            <Input
              id="glossary-translation"
              placeholder="e.g. Representation tax deduction"
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="glossary-variants">
              Rejected synonyms / terms to avoid (optional)
            </Label>
            <Input
              id="glossary-variants"
              placeholder="Comma-separated, e.g. client entertainment, per diem"
              value={variantsText}
              onChange={(e) => setVariantsText(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!canonicalTerm.trim()}
            onClick={() => {
              onSave({
                canonicalTerm: canonicalTerm.trim(),
                translation: translation.trim() || undefined,
                definition: definition.trim() || undefined,
                variants: variantsText
                  .split(",")
                  .map((v) => v.trim())
                  .filter(Boolean),
              });
            }}
          >
            Save term
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
